import CommonFunction from '@/shared/common-function';
import SystemConst from '@/shared/system-const';
import { defineComponent, onMounted, reactive, watch, toRefs, ref, nextTick } from '@vue/composition-api';

/**
 * リアクティブな変数の型定義
 */
export type reactiveData = {
    windowSize: { x: number; y: number },           // Windowサイズ
    conditions: SearchConditionType,                // 全検索条件
    allSchoolTypes: [],                             // ツリービューへ表示する校種
    grades: [],                                     // ツリービューへ表示する学年
    curriculums: [],                                // ツリービューへ表示する教科・科目
    materialTypes: [],                              // ツリービューへ表示する教材の種類
    selectAllSchoolTypes: number[],                 // ツリービューで選択した表示する校種
    selectGrades: number[],                         // ツリービューで選択した表示する学年
    selectCurriculums: string[],                    // ツリービューで選択した教科・科目
    selectMaterialTypes: string[],                  // ツリービューで選択した教材の種類
    keyword: string,                                // 入力したキーワード
    inputConditions: InputConditionType             // 選択・入力した検索条件
    showAllSchoolTypes: boolean,                    // 「校種で絞り込む」表示切替フラグ
    showGrade: boolean,                             // 「学年で絞り込む」表示切替フラグ
    showSubject: boolean,                           // 「教科・科目で絞り込む」表示切替フラグ
    showMaterialType: boolean,                      // 「教材の種類で絞り込む」表示切替フラグ
    showKeyword: boolean                            // 「キーワードで絞り込む」表示切替フラグ
};

/**
 * 引数
 */
interface Props {
    schoolTypeId: number,                   // 校種ID
    conditions: SearchConditionType         // 学年・教科・科目・教材の種類
    selectAllSchoolTypes: number[],         // 遷移前に選択していた校種
    selectGrades: number[],                 // 遷移前に選択していた学年
    selectCurriculums: string[],            // 遷移前に選択していた教科・科目
    selectMaterialTypes: string[],          // 遷移前に選択していた教材の種類
    keyword: string                         // 遷移前に入力していたキーワード
}

/**
 * 学年・教科・科目・教材の種類の型
 */
export type SearchConditionType = {
    schoolTypes: {                          // 全校種データ
        id: number,
        name: string,
        allSchoolTypes: [],
        grades: [],
        curriculums: []
    }[],                                    // 校種・学年・教科・科目
    materialTypes: []                       // 教材の種類
};

/**
 * 親コンポーネントへ渡す検索条件
 */
export type InputConditionType = {
    id: number,                             // 校種ID
    allSchoolTypes: number[],               // 「校種で絞り込む」で選択された校種ID
    grades: number[],                       // 「学年で絞り込む」で選択された学年ID
    curriculums: string[],                  // 「教科・科目で絞り込む」で選択された教科ID-科目ID（ハイフンで連結したもの）
    materialTypes: string[],                // 「教材の種類で絞り込む」で選択された条件区分-条件ID（ハイフンで連結したもの）
    keyword: string                         // 「キーワードで絞り込む」で入力されたキーワード
}

/**
 * 教材検索コンポーネント
 */
export default defineComponent({
    components: {},
    props: {
        schoolTypeId: Number,               // 校種ID
        conditions: {},                     // 学年・教科・科目・教材の種類
        selectAllSchoolTypes: [],           // 遷移前に選択していた校種
        selectGrades: [],                   // 遷移前に選択していた学年
        selectCurriculums: [],              // 遷移前に選択していた教科・科目
        selectMaterialTypes: [],            // 遷移前に選択していた教材の種類
        keyword: String                     // 遷移前に入力していたキーワード
    },
    setup(props: Props, context) {
        const form = ref<any>(null);
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },     // Windowサイズ
            conditions: {                   // 全検索条件
                schoolTypes: [],
                materialTypes: []
            },                              // 検索条件
            allSchoolTypes: [],             // ツリービューへ表示する校種
            grades: [],                     // ツリービューへ表示する学年
            curriculums: [],                // ツリービューへ表示する教科・科目
            materialTypes: [],              // ツリービューへ表示する教材の種類
            selectAllSchoolTypes: [],       // ツリービューで選択した表示する校種
            selectGrades: [],               // ツリービューで選択した表示する学年
            selectCurriculums: [],          // ツリービューで選択した教科・科目
            selectMaterialTypes: [],        // ツリービューで選択した教材の種類
            keyword: '',                    // 入力したキーワード
            inputConditions: {              // 選択・入力した検索条件
                id: 0,
                allSchoolTypes: [],
                grades: [],
                curriculums: [],
                materialTypes: [],
                keyword: ''
            },
            showAllSchoolTypes: false,      // 「校種で絞り込む」表示切替フラグ
            showGrade: false,               // 「学年で絞り込む」表示切替フラグ
            showSubject: false,             // 「教科・科目で絞り込む」表示切替フラグ
            showMaterialType: false,        // 「教材の種類で絞り込む」表示切替フラグ
            showKeyword: false              // 「キーワードで絞り込む」表示切替フラグ
        });

        // バリデーション用最大桁数
        const maxLength = {
            keyword: SystemConst.MATERIAL_SEARCH_MAX_LENGTH['KEYWORD']  // キーワード
        }

        // キーワードのバリデーション
        const keywordRules: any[] = [
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['keyword'], 'キーワード')
        ];

        // 親コンポーネントから検索条件を取得する
        state.conditions = props.conditions;

        // 校種IDが設定された場合ツリービューを更新する
        const { schoolTypeId } = toRefs(props);
        watch(schoolTypeId, () => {
            setTreeView();
        });

        // 検索条件が設定された場合ツリービューを更新する
        const { conditions } = toRefs(props);
        watch(conditions, () => {
            state.conditions = conditions.value;
            setTreeView();
            // 親コンポーネントのrestoreイベントを発火して親画面で検索条件を復元する
            nextTick(() => { context.emit('restore', schoolTypeId.value); });
        });

        // selectAllSchoolTypesが設定された場合、校種の選択状態を復元する
        const { selectAllSchoolTypes } = toRefs(props);
        watch(selectAllSchoolTypes, () => {
            state.selectAllSchoolTypes = selectAllSchoolTypes.value;
            // 校種が選択されている場合
            // 「校種で絞り込む」表示切替フラグにtrueを設定する
            if (state.selectAllSchoolTypes.length !== 0) {
                state.showAllSchoolTypes = true;
            }
        });

        // selectGradesが設定された場合、学年の選択状態を復元する
        const { selectGrades } = toRefs(props);
        watch(selectGrades, () => {
            state.selectGrades = selectGrades.value;
            // 学年が選択されている場合
            // 「学年で絞り込む」表示切替フラグにtrueを設定する
            if (state.selectGrades.length !== 0) {
                state.showGrade = true;
            }
        });

        // selectCurriculumsが設定された場合、教科・科目の選択状態を復元する
        const { selectCurriculums } = toRefs(props);
        watch(selectCurriculums, () => {
            state.selectCurriculums = selectCurriculums.value;
            // 教科・科目が選択されている場合
            // 「教科・科目で絞り込む」表示切替フラグにtrueを設定する
            if (state.selectCurriculums.length !== 0) {
                state.showSubject = true;
            }
        });

        // selectMaterialTypesが設定された場合、教材の種類の選択状態を復元する
        const { selectMaterialTypes } = toRefs(props);
        watch(selectMaterialTypes, () => {
            state.selectMaterialTypes = selectMaterialTypes.value;
            // 教材の種類が選択されている場合
            // 「教材の種類で絞り込む」表示切替フラグにtrueを設定する
            if (state.selectMaterialTypes.length !== 0) {
                state.showMaterialType = true;
            }
        });

        // keywordが設定された場合、キーワードを復元する
        const { keyword } = toRefs(props);
        watch(keyword, () => {
            state.keyword = keyword.value;
            // キーワードが設定されている場合
            // 「キーワードで絞り込む」表示切替フラグにtrueを設定する
            if (state.keyword) {
                state.showKeyword = true;
            }
        });

        /**
         * 初期処理
         */
        onMounted(() => {
            // Windowサイズの初期化
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };

            // ツリービューの更新
            setTreeView();
        });

        /**
         * ツリービューの更新
         */
        function setTreeView() {
            // 引数に指定された校種IDと検索条件から必要なデータをtreeviewへ設定する
            const schoolTypeIndex = state.conditions.schoolTypes.findIndex(
                x => x.id === Number(props.schoolTypeId));
            if (schoolTypeIndex !== -1) {
                state.allSchoolTypes = state.conditions.schoolTypes[schoolTypeIndex].allSchoolTypes;
                state.grades = state.conditions.schoolTypes[schoolTypeIndex].grades;
                state.curriculums = state.conditions.schoolTypes[schoolTypeIndex].curriculums;
            }
            state.materialTypes = state.conditions.materialTypes;
        }

        /**
         * windowサイズ設定
         * 
         * @param windowSize Windowサイズ
         */
        async function resize(windowSize: any) {
            state.windowSize = windowSize;
        }

        /**
         * 条件クリアボタン押下時処理
         */
        function clear() {
            // 検索条件エリア内のチェックボックスを全て未チェック状態にする
            state.selectAllSchoolTypes = [];
            state.selectGrades = [];
            state.selectCurriculums = [];
            state.selectMaterialTypes = [];
            // キーワード入力欄をクリアする
            state.keyword = '';
        }

        /**
         * 検索ボタン押下時処理
         */
        function search() {
            // 検索キーワードのバリデーション
            if (!form.value.validate()) {
                return;
            }

            // 選択されているチェックボックスのID及び検索キーワードを取得し、state.inputConditionsへ設定する
            state.inputConditions = {
                id: Number(schoolTypeId.value),
                allSchoolTypes: state.selectAllSchoolTypes,
                grades: state.selectGrades,
                curriculums: state.selectCurriculums,
                materialTypes: state.selectMaterialTypes,
                keyword: state.keyword
            };

            // 親コンポーネントのupdateイベントを発火
            context.emit('update', state.inputConditions);
        }

        return {
            state,
            form,
            maxLength,
            keywordRules,
            resize,
            clear,
            search
        };
    }
});
